import { GUDHUB_SETTINGS } from '../../../../gudhub_settings.js';

export const messagingMixin = {

    //********************* SEND MESSAGE ********************************//
    // Sends the markdown from the compose editor and/or any staged file
    // attachments as new messages in the active conversation (dm or thread),
    // then renders each locally. Text is sent first; files follow individually.
    async sendMessage() {
        const rawMarkdown = this.tuiEditor ? this.tuiEditor.getMarkdown().trim() : '';
        // Toast UI WYSIWYG returns "<br>" and zero-width chars for an empty editor — strip them to detect truly empty input
        const markdown = rawMarkdown.replace(/<[^>]*>/g, '').replace(/[​-‍﻿]/g, '').trim();

        if(!markdown.length && !this.pendingAttachments.length) return;

        // viewMode 'no_discussion' has no conversation yet (see
        // conversationMixin.js's showNoDiscussion) - create the item-thread
        // first (openItemConversation re-renders into viewMode 'thread' via
        // enterItemThread, so the editor/loader/send button below are
        // re-queried fresh rather than reused from before the switch).
        if(this.viewMode === 'no_discussion') await this.openItemConversation();

        const loader = this.querySelector('.send_message_loader');
        const button = this.querySelector('.send_button');

        loader.style.display = 'block';
        button.setAttribute('disabled', '');

        if(markdown.length) {
            const { content, mentions } = this.extractMentionIds(markdown);
            const body = { user_id: this.activeUserId, user_name: gudhub.storage.getUser().fullname, type: 'text', content };
            if(mentions.length) body.mentions = mentions;
            await this.postMessage(body);
            this.tuiEditor.setMarkdown('');
            this.clearMentionState();
        }

        if(this.pendingAttachments.length) {
            await Promise.allSettled(this.pendingAttachments.map(a => a.uploadPromise));

            for(const a of this.pendingAttachments) {
                if(a.status !== 'ready') continue;
                const { serverFile } = a;
                await this.postMessage({
                    user_id: this.activeUserId,
                    user_name: gudhub.storage.getUser().fullname,
                    type: 'file',
                    content: {
                        filename: serverFile.filename,
                        thumbnail_filename: serverFile.thumbnail_filename || null,
                        name: serverFile.original_name,
                        size: serverFile.size,
                        mime_type: serverFile.mime_type
                    }
                });
            }

            this.pendingAttachments = [];
            this.renderAttachmentCards();
        }

        loader.style.display = 'none';
        button.removeAttribute('disabled');
    },

    //********************* POST MESSAGE ********************************//
    // Sends a single message body to the server, normalizes and renders it
    // locally, and scrolls the chat to the bottom.
    // * body (Object) — { user_id, type, content }
    async postMessage(body) {
        const response = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/${this.conversationId}/message`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });

        const savedMessage = await response.json();

        if(!savedMessage.message_id) {
            this.showErrorMessage(savedMessage.message || 'Failed to send message');
            return;
        }

        const message = this.normalizeMessage(savedMessage);
        this.activeMessages().push(message);
        this.addMessageToConversation(message);
        this.scrollChatToBottom();
        this.notifyIfJustJoinedThread();
    },

    //********************* NOTIFY IF JUST JOINED THREAD ********************************//
    // addMessage() auto-joins the sender to an item-thread's participants the
    // first time they reply to one they were only previewing (read-only,
    // non-participant). notifyParticipants excludes the sender, so without this
    // the sidebar's "Item Discussions" list wouldn't pick up the newly-joined
    // thread until a reload - tell it directly via item_thread_discussed.
    notifyIfJustJoinedThread() {
        if(this.viewMode !== 'thread' || this.threadState?.kind !== 'item') return;

        const participants = this.threadState.participants.map(String);
        if(participants.includes(this.activeUserId)) return;

        this.threadState.participants = [...participants, this.activeUserId];

        this.dispatchEvent(new CustomEvent('item_thread_discussed', {
            bubbles: true,
            composed: true,
            detail: {
                conversation_id: this.conversationId,
                item_ref: this.threadState.itemRef,
                unread_count: 0,
                participants: this.threadState.participants
            }
        }));
    },

    showErrorMessage(message) {
        const template = `<md-toast>
                <p>${message}</p>
                <div class="gh-btn" gh-icon="cross 0893d2 30px normal" ng-click="closeToast()"></div>
            </md-toast>`;

        this.toast.customToast({
            template,
            hideDelay: 10000,
            scope: this.scope.$new(true)
        });
    },
};
